import { observable, action, makeObservable } from "mobx";
import { onActivated } from "../utils/ExcelRun";
import { currentSheetHasReport, getSheetContext } from "../utils/PwOffice";
import { ReportOption, ParameterPayload } from '../types';
import { storage, STORAGE_KEYS } from "../utils/Storage";

export default class UIStore {
  @observable section: string = "login";
  @observable sectionTitle? = "Generar reportes";
  @observable isLoaded = false;
  @observable reportSelected: ReportOption | null = null;
  @observable sheetTitle = { SheetName: "", SheetID: "" };
  @observable parametersSaved: ParameterPayload[] = [];
  @observable existingReport = false;
  @observable logOutUrl = "";
  @observable isLogin = false;
  @observable modalText = { Title: "", Description: "" };
  @observable showEmptyDialog = false;
  @observable forceReplaceSheet = false;
  @observable tenantName: string = storage.getItem(STORAGE_KEYS.TENANT_NAME) ?? "";
  @observable userRole: string = storage.getItem(STORAGE_KEYS.USER_ROLE) ?? "";
  @observable userName: string = storage.getItem(STORAGE_KEYS.USER_NAME) ?? "";

  constructor() {
    makeObservable(this);
  }

  titles: any = {
    import: "Generar reportes",
    setting: "Ajustes",
  };

  @action setTextForUpdateTable() {
    this.modalText = {
      Title: "¿Remplazar hoja actual?",
      Description: `Está a punto de remplazar toda la
      información de la hoja actual. ¿Está seguro
      de que desea continuar?`,
    };
  }

  @action dismissDialogEmpty() {
    this.showEmptyDialog = false;
  }

  @action setTextForCreateSheet() {
    this.modalText = {
      Title: "¿Crear hoja nueva?",
      Description: `Existe un tabla en la hoja actual, al crear el nuevo reporte se creara una nueva hoja.`,
    };
  }

  @action changeLoginStatus(status: boolean) {
    this.isLogin = status;
  }

  @action async onActivatedSheet(...args: any) {
    this.sheetTitle = await getSheetContext();
    await this.checkIfExistsReport(true);
  }

  @action async setSheetTitle() {
    this.sheetTitle = await getSheetContext();
    this.isLoaded = true;
    await onActivated((...args) => this.onActivatedSheet(...args));
    // await this.checkIfExistsReport();
  }

  @action setParameterSaved(parameters: ParameterPayload[]) {
    this.existingReport = true;
    this.parametersSaved = parameters;
  }

  // sheetSwitched=true when the user navigated to a different sheet (always redirect based on sheet content)
  // sheetSwitched=false (default) when called from re-init or login flow (don't redirect away from 'report')
  @action async checkIfExistsReport(sheetSwitched = false) {
    const reportExisting = await currentSheetHasReport();
    if (reportExisting) {
      if (this.section !== "login") {
        this.setReportSelected(reportExisting.ReportInfo);
        await this.changeSection("report");
        this.parametersSaved = reportExisting.Parameters;
        this.existingReport = true;
      }
    } else {
      this.existingReport = false;
      this.setReportSelected(null);
      if (this.section !== "login" && (sheetSwitched || this.section !== "report")) {
        await this.changeSection("import");
      }
    }
  }

  @action setTenantName(name: string) {
    this.tenantName = name;
    storage.setItem(STORAGE_KEYS.TENANT_NAME, name);
  }

  @action setUserRole(role: string) {
    this.userRole = role;
    storage.setItem(STORAGE_KEYS.USER_ROLE, role);
  }

  @action setUserName(name: string) {
    this.userName = name;
    storage.setItem(STORAGE_KEYS.USER_NAME, name);
  }

  @action setReportSelected(reportSelected: ReportOption | null) {
    this.reportSelected = reportSelected;
  }

  @action async changeSection(section: string) {
    if (this.section === "report" && section === "import") {
      this.parametersSaved = [];
      this.existingReport = false;
    }

    if (this.section === "login" && section === "import") {
      this.section = section;
      await this.checkIfExistsReport();
      return;
    }

    this.section = section;

    if (section in this.titles) {
      this.sectionTitle = this.titles[section];
    } else if (section === "report") {
      this.sectionTitle = this.reportSelected?.text ?? "Reporte";
    }
  }

  @action activateForceReplaceSheet() {
    this.forceReplaceSheet = true;
  }
}
